/**
 * ふるさと納税の控除上限額（バックエンド `FurusatoNouzeiService` の移植 + #63 拡張）。
 *
 * 計算式（制度上の上限。住宅ローン控除を考慮しない）:
 *   控除上限 = 住民税所得割額 × 20% ÷ (90% − 所得税の限界税率 × (1 + 付加税率)) + 2,000円
 *
 * `1 + 付加税率`（= 1.021）は復興特別所得税（2027 は防衛特別所得税を含む）を考慮する。
 * 分母が 0 以下の場合は 0 を返す（防御）。
 *
 * #63 では住宅ローン控除（税額控除）を踏まえた「損しない上限」を追加する。
 * 住民税所得割の税額控除は 調整控除 → 配当控除 → 住宅借入金等特別税額控除 →
 * 寄附金税額控除 → 外国税額控除 の順に行われる（自治体周知）。住宅ローン控除が先に
 * 所得割を消費するため、寄附金税額控除（基本分 + 特例分）が全額入らないことがある。
 * また確定申告では所得税を減らすことで住宅ローン控除が所得税で引ききれず住民税へ押し
 * 出され、控除可能額の使途が変わる。本モジュールはこの相互作用を考慮して、自己負担が
 * 2,000 円を超えない最大の寄附額を求める。
 *
 * 出典:
 * - 国税庁 No.1155 ふるさと納税（寄附金控除）
 * - 総務省「ふるさと納税のしくみ」
 * - 自治体周知（横浜市「税額控除(令和8年度課税以降）」等）の税額控除の順序・限度額
 */

import type { FurusatoFilingMethod, TaxRules } from './types';

/** 自己負担額。 */
export const SELF_BURDEN = 2000;

/**
 * 住民税所得割額を計算する（均等割を除く。円未満切捨て）。
 */
export function residentTaxIncomeLevy(residentTaxableIncome: number, rules: TaxRules): number {
  if (residentTaxableIncome <= 0) {
    return 0;
  }
  const ratePercent = Math.round(rules.residentTaxRate() * 100);
  return Math.floor((residentTaxableIncome * ratePercent) / 100);
}

/**
 * ふるさと納税の特例控除分（自己負担額を除く）を計算する。
 *
 * 分母 = 90% − 所得税の限界税率 × (1 + 付加税率)。分母が 0 以下の場合は 0 を返す。
 */
export function calculateSpecialDeduction(
  levy: number,
  marginalTaxRate: number,
  rules: TaxRules,
): number {
  if (levy <= 0) {
    return 0;
  }
  // すべて整数の有理数演算でバックエンド（BigDecimal）と一致させる。
  //   rate = r/100, surtax = s/1000 のとき
  //   denominator = 9/10 − (r/100)(1 + s/1000) = (90000 − r(1000+s)) / 100000
  //   special = floor( levy × (2/10) ÷ denominator )
  //           = floor( levy × 20000 ÷ (90000 − r(1000+s)) )
  const r = Math.round(marginalTaxRate * 100);
  const s = Math.round(rules.surtaxRate() * 1000);
  const denominator = 90000 - r * (1000 + s);
  if (denominator <= 0) {
    return 0;
  }
  return Math.floor((levy * 20000) / denominator);
}

/**
 * ふるさと納税の控除上限額（自己負担 2,000 円を含む）。
 */
export function calculateFurusatoLimit(
  residentTaxableIncome: number,
  marginalTaxRate: number,
  rules: TaxRules,
): number {
  const levy = residentTaxIncomeLevy(residentTaxableIncome, rules);
  return calculateSpecialDeduction(levy, marginalTaxRate, rules) + SELF_BURDEN;
}

/** 「損しない上限」の算出入力（計算本体と明細で共有する）。 */
export interface FurusatoSafeLimitInput {
  /** 制度上の控除上限額（`calculateFurusatoLimit` の値。住宅ローン控除を考慮しない）。 */
  institutionalLimit: number;
  /** 所得税額（住宅ローン控除などの税額控除前）。 */
  incomeTax: number;
  /** 所得税の限界税率。 */
  marginalTaxRate: number;
  /** 調整控除後の住民税所得割額（円）。 */
  residentIncomeLevy: number;
  /** 住宅ローン控除の可能額（円）。0 なら住宅ローン控除なし。 */
  housingLoanCredit: number;
  /** 住宅ローン控除の住民税分の限度額（円）。 */
  housingLoanResidentLimit: number;
  /** ふるさと納税の申告方法（確定申告 / ワンストップ特例）。 */
  filingMethod: FurusatoFilingMethod;
  rules: TaxRules;
}

/** 「損しない上限」の算出結果。 */
export interface FurusatoSafeLimitResult {
  /** 制度上の控除上限額（確定申告ベース。住宅ローン控除を考慮しない）。 */
  institutionalLimit: number;
  /** 住宅ローン控除と申告方法を踏まえた「損しない上限」。 */
  safeLimit: number;
  /** 制度上の上限 − 損しない上限（損する可能性のある差額）。 */
  difference: number;
  /** 損しない上限の算出に用いた申告方法。 */
  filingMethod: FurusatoFilingMethod;
  /** 制度上の上限まで寄附した場合に自己負担 2,000 円を超える額（損する額）。 */
  lossAtInstitutionalLimit: number;
}

/**
 * 住宅ローン控除を踏まえた「損しない上限」を計算する。
 *
 * 寄附額に対する実際の控除額（所得税 + 住民税）を評価し、自己負担が 2,000 円を
 * 超えない最大の寄附額を二分探索で求める。控除額は寄附額に対して単調増加（増分 1 以下）
 * のため、自己負担超過額も単調となり二分探索が成立する。
 *
 * 制約:
 * 1. 特例控除分は制度上の上限を算定したときの水準（調整控除後の所得割額の 20% 相当）が上限。
 * 2. 寄附金税額控除（基本分 + 特例分）は、住宅借入金等特別税額控除後の所得割額に収まること。
 * 3. 確定申告では寄附金控除が所得税を減らし、住宅ローン控除が住民税へ押し出される。
 *    その押し出しが住民税分の限度額を超えると所得税分の控除が実現しない（取り合い）。
 * 4. ワンストップ特例では所得税から控除せず、所得税分相当が申告特例控除として住民税から控除される。
 *
 * 円未満切捨てによる数円の誤差で境界がぶれないよう、控除額は連続値（小数）で評価する。
 */
export function calculateFurusatoSafeLimit(input: FurusatoSafeLimitInput): FurusatoSafeLimitResult {
  const {
    institutionalLimit,
    incomeTax,
    marginalTaxRate,
    residentIncomeLevy,
    housingLoanCredit,
    housingLoanResidentLimit,
    filingMethod,
    rules,
  } = input;

  const rPercent = Math.round(marginalTaxRate * 100);
  const surtaxMilli = Math.round(rules.surtaxRate() * 1000);
  const incomeRateMilli = rPercent * (1000 + surtaxMilli); // 所得税率 × (1 + 付加税率) × 100000
  const specialDenominator = 90000 - incomeRateMilli; // 特例控除割合の分母（×100000）
  const incomeRate = incomeRateMilli / 100000;
  const specialRate = specialDenominator > 0 ? specialDenominator / 100000 : 0;
  const levy = Math.max(0, residentIncomeLevy);
  const upper = Math.max(0, Math.floor(institutionalLimit));
  // 制度上の上限に対応する自己負担を除いた寄附額。特例控除分の上限も同時に定まる。
  const capAmount = Math.max(0, upper - SELF_BURDEN);
  const specialCap = capAmount * specialRate;
  const housingCredit = Math.max(0, housingLoanCredit);

  /** 住宅ローン控除を所得税額 t のもとで所得税・住民税へ配分する（連続値）。 */
  const housingSplit = (t: number): { incomeCredit: number; residentCredit: number } => {
    const incomeCredit = Math.min(housingCredit, Math.max(0, t));
    const residentCredit = Math.min(
      housingCredit - incomeCredit,
      Math.max(0, housingLoanResidentLimit),
      levy,
    );
    return { incomeCredit, residentCredit };
  };

  const baseline = housingSplit(incomeTax);
  const baselineTax =
    Math.max(0, incomeTax - baseline.incomeCredit) + (levy - baseline.residentCredit);

  const safeSpecial = (amount: number): number => Math.min(amount * specialRate, specialCap);

  /** 寄附額（自己負担を除く amount）に対する税額合計（均等割・付加税を除く）。 */
  const totalTaxForAmount = (amount: number): number => {
    const basic = amount * 0.1;
    if (filingMethod === 'FINAL_RETURN') {
      // 寄附金控除は所得控除のため、所得税額が減り、住宅ローン控除が住民税へ押し出される。
      const incomeReduction = amount * incomeRate;
      const incomeTaxAfter = Math.max(0, incomeTax - incomeReduction);
      const housing = housingSplit(incomeTaxAfter);
      const budget = Math.max(0, levy - housing.residentCredit);
      const residentCredit = Math.min(basic + safeSpecial(amount), budget);
      return (
        Math.max(0, incomeTaxAfter - housing.incomeCredit) +
        (levy - housing.residentCredit - residentCredit)
      );
    }

    // ワンストップ特例: 所得税分相当が申告特例控除として住民税から控除される。
    const housing = housingSplit(incomeTax);
    const budget = Math.max(0, levy - housing.residentCredit);
    const special = safeSpecial(amount);
    const reportingSpecial = specialRate > 0 ? special * (incomeRate / specialRate) : 0;
    const residentCredit = Math.min(basic + special + reportingSpecial, budget);
    return (
      Math.max(0, incomeTax - housing.incomeCredit) +
      (levy - housing.residentCredit - residentCredit)
    );
  };

  /** 自己負担が 2,000 円を超える額（0 以下なら損しない）。 */
  const loss = (amount: number): number =>
    Math.max(0, amount - (baselineTax - totalTaxForAmount(amount)));

  let safe = 0;
  if (capAmount > 0) {
    let low = 0;
    let high = capAmount;
    let best = 0;
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      if (loss(mid) <= 1e-6) {
        best = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    safe = best > 0 ? best + SELF_BURDEN : 0;
  }

  return {
    institutionalLimit: upper,
    safeLimit: Math.min(safe, upper),
    difference: Math.max(0, upper - safe),
    filingMethod,
    lossAtInstitutionalLimit: capAmount > 0 ? loss(capAmount) : 0,
  };
}
